import { useQueryClient } from "@tanstack/react-query";
import { createContext, useCallback, useEffect, useMemo, useRef, type ReactNode } from "react";

import {
  AUTH_ME_QUERY_KEY,
  fetchMe,
  useLoginMutation,
  useLogoutMutation,
  useMeQuery,
  useSwitchTenantMutation,
  type MeResponse,
} from "@/api/auth";

export type AuthStatus = "loading" | "authenticated" | "anonymous";

export interface AuthContextValue {
  status: AuthStatus;
  /** authenticated disindaki her durumda null. */
  principal: MeResponse | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  switchTenant: (tenantId: number) => Promise<void>;
  refresh: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/**
 * Oturum durumunu /api/v2/auth/me uzerinden yonetir (TanStack Query ile
 * onbelleklenir). Tek AKTIF uyeligi olan ve is_super OLMAYAN kullanicilar
 * icin aktif tenant yoksa otomatik tek uyeliğe gecer (tek marka kararindan:
 * kullanicinin /tenant-sec ekraninda takilmasina gerek yok). Bekleyen
 * ('pending') uyelikler bu sayima girmez — backend switch-tenant'i 403 ile
 * reddeder (auth.py) ve otomatik geciş denemesi tek seferle sinirlidir
 * (autoSwitchedRef basarisizlikta SIFIRLANMAZ), aksi halde switchMutation
 * referansi her durum degisiminde yenilenip efekti tekrar tetikler ve
 * switch-tenant'a sonsuz istek dongusune yol acar.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const meQuery = useMeQuery();
  const loginMutation = useLoginMutation();
  const logoutMutation = useLogoutMutation();
  const switchMutation = useSwitchTenantMutation();
  const autoSwitchedRef = useRef(false);

  const status: AuthStatus = meQuery.isPending ? "loading" : meQuery.isError ? "anonymous" : "authenticated";
  const principal = status === "authenticated" ? (meQuery.data ?? null) : null;

  const activeMemberships = useMemo(
    () => principal?.tenants.filter((membership) => membership.status === "active") ?? [],
    [principal],
  );

  useEffect(() => {
    if (
      status === "authenticated" &&
      principal &&
      !principal.active_tenant &&
      !principal.user.is_super &&
      activeMemberships.length === 1 &&
      !autoSwitchedRef.current
    ) {
      autoSwitchedRef.current = true;
      switchMutation.mutate(activeMemberships[0].tenant_id, {
        onSuccess: () => void queryClient.invalidateQueries({ queryKey: AUTH_ME_QUERY_KEY }),
      });
    }
  }, [status, principal, activeMemberships, switchMutation, queryClient]);

  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: AUTH_ME_QUERY_KEY });
  }, [queryClient]);

  const login = useCallback(
    async (email: string, password: string) => {
      await loginMutation.mutateAsync({ email, password });
      autoSwitchedRef.current = false;
      await refresh();
    },
    [loginMutation, refresh],
  );

  const logout = useCallback(async () => {
    await logoutMutation.mutateAsync();
    autoSwitchedRef.current = false;
  }, [logoutMutation]);

  const switchTenant = useCallback(
    async (tenantId: number) => {
      await switchMutation.mutateAsync(tenantId);
      await refresh();
    },
    [switchMutation, refresh],
  );

  const value = useMemo<AuthContextValue>(
    () => ({ status, principal, login, logout, switchTenant, refresh }),
    [status, principal, login, logout, switchTenant, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Testler / SSR-disi kosumlar icin dogrudan cagirilabilir yardimci. */
export function fetchPrincipal(): Promise<MeResponse> {
  return fetchMe({ noRedirect: true });
}
